import { Building2, CalendarClock, Landmark, Route } from "lucide-react";

import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import useTranslations from "@/shared/lang/useTranslations";

import type { TaxCorridorFilter } from "../../utils/taxCoverage";

export interface TaxCorridorKpis {
  total: number;
  taxed: number;
  own: number;
  scheduled: number;
}

export interface TaxKpiStripProps {
  kpis: TaxCorridorKpis;
  filter: TaxCorridorFilter;
  onFilterChange: (filter: TaxCorridorFilter) => void;
  scoped: boolean;
  unavailable: boolean;
}

/** The strip IS the corridors filter, as in the price book. */
export function TaxKpiStrip({ kpis, filter, onFilterChange, scoped, unavailable }: TaxKpiStripProps) {
  const { t } = useTranslations("paymentTaxes");
  const value = (count: number) => (unavailable ? "—" : String(count));

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <ColocaKpiCard
        label={t("kpi_all_label")}
        value={value(kpis.total)}
        icon={Route}
        accent="primary"
        meta={unavailable ? t("kpi_unavailable") : t("kpi_all_meta", { taxed: kpis.taxed })}
        selected={filter === "all"}
        onSelect={() => onFilterChange("all")}
      />
      <ColocaKpiCard
        label={t("kpi_taxed_label")}
        value={value(kpis.taxed)}
        icon={Landmark}
        accent={kpis.taxed > 0 ? "info" : "neutral"}
        meta={t("kpi_taxed_meta")}
        selected={filter === "taxed"}
        onSelect={() => onFilterChange("taxed")}
      />
      <ColocaKpiCard
        label={t("kpi_own_label")}
        value={value(kpis.own)}
        icon={Building2}
        accent={kpis.own > 0 ? "info" : "neutral"}
        meta={t(scoped ? "kpi_own_meta_org" : "kpi_own_meta_general")}
        selected={filter === "own"}
        onSelect={() => onFilterChange("own")}
      />
      <ColocaKpiCard
        label={t("kpi_scheduled_label")}
        value={value(kpis.scheduled)}
        icon={CalendarClock}
        accent={kpis.scheduled > 0 ? "info" : "neutral"}
        meta={t("kpi_scheduled_meta")}
        selected={filter === "scheduled"}
        onSelect={() => onFilterChange("scheduled")}
      />
    </div>
  );
}
